import { useEffect, useState } from "react";
import axios from "axios";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useLang } from "@/i18n/LangContext";
import { it, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function expandRange(checkIn, checkOut) {
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const days = [];
  const d = new Date(start);
  while (d < end) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export default function Availability({ range, setRange }) {
  const { t, lang } = useLang();
  const [bookedDays, setBookedDays] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then((r) => {
        const days = r.data.flatMap((b) => expandRange(b.check_in, b.check_out));
        setBookedDays(days);
      })
      .catch(() => {});
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <section
      id="availability"
      data-testid="availability-section"
      className="py-24 sm:py-32 bg-[#F0EDE6]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-overline text-[#0097b2] mb-4">
              {t.availability.overline}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#121C26]">
              {t.availability.title}
            </h2>
            <p className="mt-6 text-[#4A5D6B] max-w-md leading-relaxed">
              {t.availability.subtitle}
            </p>

            <div className="mt-10 space-y-3 text-sm">
              <div className="flex items-center gap-3" data-testid="legend-available">
                <span className="w-4 h-4 border border-[#1A3B4D]/30 bg-white inline-block" />
                <span className="text-[#4A5D6B]">{t.availability.legendAvailable}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="legend-booked">
                <span className="w-4 h-4 bg-[#e7c4c4] inline-block" />
                <span className="text-[#4A5D6B]">{t.availability.legendBooked}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="legend-selected">
                <span className="w-4 h-4 bg-[#1A3B4D] inline-block" />
                <span className="text-[#4A5D6B]">{t.availability.legendSelected}</span>
              </div>
            </div>

            {range?.from && range?.to && (
              <div
                data-testid="selected-range"
                className="mt-8 p-5 bg-white border border-[#E0DACE]"
              >
                <p className="text-xs uppercase tracking-overline text-[#0097b2] mb-2">
                  {t.availability.selectedRange}
                </p>
                <p className="font-serif text-xl text-[#121C26]">
                  {range.from.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")} →{" "}
                  {range.to.toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
                </p>
                <button
                  data-testid="clear-range"
                  onClick={() => setRange(undefined)}
                  className="mt-3 text-sm text-[#1A3B4D] underline underline-offset-4 hover:text-[#102632]"
                >
                  {t.availability.clear}
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 bg-white border border-[#E0DACE] p-6 sm:p-10" data-testid="availability-calendar">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              numberOfMonths={2}
              disabled={[{ before: today }, ...bookedDays]}
              modifiers={{ booked: bookedDays }}
              modifiersClassNames={{ booked: "rdp-day_booked" }}
              locale={lang === "it" ? it : enUS}
              styles={{
                caption: { color: "#121C26", fontFamily: "Cormorant Garamond" },
                head: { color: "#0097b2" },
              }}
              className="rdp-amac"
            />
          </div>
        </div>
      </div>

      <style>{`
        .rdp-amac { --rdp-accent-color: #1A3B4D; --rdp-background-color: #F0EDE6; }
        .rdp-amac .rdp-day_selected, .rdp-amac .rdp-day_selected:hover { background-color: #1A3B4D; color: #FAF9F6; border-radius: 2px; }
        .rdp-amac .rdp-day_range_middle { background-color: #DCE4E8 !important; color: #1A3B4D !important; }
        .rdp-amac .rdp-day_range_start, .rdp-amac .rdp-day_range_end { background-color: #1A3B4D !important; color: #FAF9F6 !important; }
        .rdp-amac .rdp-caption_label { font-family: 'Cormorant Garamond'; font-size: 1.4rem; }
      `}</style>
    </section>
  );
}
